import type { SSEEvent } from "../../types";
import type { EditorSaveToastContext } from "../../utils/editor";

export interface SaveSseTrackedJobLike {
  path: string;
  suppressSaveSyncedToast: boolean;
  saveContext: EditorSaveToastContext;
  saveAttemptId?: string;
  reason?: string;
  originMutationId?: string;
}

export interface SaveSseActiveEntryLike {
  suppressSaveSyncedToast: boolean;
  saveContext: EditorSaveToastContext;
  saveAttemptId?: string;
  reason?: string;
  originMutationId?: string;
}

/**
 * ViewR 저장 완료로 처리할 SSE 여부.
 * 백엔드 정본은 `type: "viewr_update_complete"` (routers/sse.broadcast_viewr_update_complete).
 * `status`/`success`만 있는 페이로드는 다른 작업·노이즈와 충돌할 수 있어 제외한다.
 */
export const isCompletedViewRUpdateEvent = (data: SSEEvent): boolean => {
  if (
    data.type === "viewr_update_complete" ||
    data.type === "viewr_update_completed"
  ) {
    return true;
  }

  const jobIdRaw = (data as { job_id?: unknown }).job_id;
  const jobId =
    typeof jobIdRaw === "string" && jobIdRaw.trim() !== "" ? jobIdRaw.trim() : "";
  if (!jobId) {
    return false;
  }

  return (
    data.type === "job_completed" ||
    data.type === "completed" ||
    data.event === "job_completed"
  );
};

export const shouldIgnoreViewRUpdateCompletedEvent = ({
  trackedJobId,
  receivedJobId,
}: {
  trackedJobId?: string | null;
  receivedJobId?: string | null;
}): boolean =>
  Boolean(
    trackedJobId &&
      typeof receivedJobId === "string" &&
      receivedJobId.trim() !== "" &&
      receivedJobId !== trackedJobId,
  );

export const resolveSaveSseUiState = ({
  trackedJob,
  activeEntryBeforeRelease,
}: {
  trackedJob?: SaveSseTrackedJobLike;
  activeEntryBeforeRelease?: SaveSseActiveEntryLike;
}): {
  suppressSaveSyncedToastResolved: boolean;
  saveContextForThumbnail: EditorSaveToastContext;
  pathForRelease?: string;
} => ({
  suppressSaveSyncedToastResolved:
    trackedJob !== undefined
      ? trackedJob.suppressSaveSyncedToast
      : activeEntryBeforeRelease?.suppressSaveSyncedToast === true,
  saveContextForThumbnail:
    trackedJob?.saveContext ?? activeEntryBeforeRelease?.saveContext ?? "default",
  pathForRelease: trackedJob?.path,
});

export const buildSavePersistedEventDetail = ({
  gcsPath,
  jobId,
  source,
  suppressToast,
  skipThumbnailAfterPersist,
  saveAttemptId,
  reason,
  originMutationId,
  signature,
}: {
  gcsPath: string;
  jobId?: string;
  source: string;
  suppressToast?: boolean;
  skipThumbnailAfterPersist?: boolean;
  saveAttemptId?: string;
  reason?: string;
  originMutationId?: string;
  signature?: string;
}) => ({
  gcsPath,
  jobId,
  timestamp: Date.now(),
  source,
  suppressToast,
  skipThumbnailAfterPersist,
  saveAttemptId,
  reason,
  originMutationId,
  signature,
});

export const buildSaveTimeoutResult = () => ({
  status: "completed",
  progress: "100",
  note: "타임아웃으로 인해 완료로 간주됨",
});
